import React, { useState, useEffect, useMemo, useRef } from "react";
import * as dateFns from "date-fns";
import { useTranslation } from "react-i18next";

import Modal from "../../common/Modal";
import Button from "../../common/FormFields/Button";
import Search from "../../common/FormFields/Search";
import CustomSelect from "../../common/FormFields/Select";
import ForceConfirmModal from "./ForceConfirmModal";
import {
  prepareTimeslotForUpdating,
} from "./helpers";
import {
  getStartOfWorkDay,
  getTimeIntervals,
  timestampToIso,
  timestampToWorkingTime,
  workingTimeToTimestamp,
  getAppointmentTypeDisplayName,
} from "../../../services/helpers";
import { ALL_BLOOD_TYPES, BLOOD_TITER } from "./constants";
import { get, preCheckBooking } from '../../../services/fetch';

const BookAppointment = (props) => {
  const {
    selectedTimeslot,
    bloodBankId,
    bloodBankName,
    bookAppointmentModalIsOpen,
    toggleBookAppointmentModal,
    userByPnr,
    isOverwriting,
    getUserByPnr,
    bookAppointment,
    cancelAppointmentBooking,
    appointmentTypes,
    startOverwriteTimeslot,
    finishOverwriteTimeslot,
    currentAppointment,
    isDayPage,
    updateDonor,
    isSlotClickFromCell,
    selectedTime,
    selectedResourceId,
    currentDate,
    setTimeslotRequest,
    setIsDirectlyBooking,
    calendar,
  } = props;
  const { t } = useTranslation();
  const [userPnr, setUserPnr] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAppointments, setCurrentAppointments] = useState([]);
  const [chosenAppointment, setChosenAppointment] = useState(null);
  const [isDonorStatusPending, setIsDonorStatusPending] = useState(false);
  const [isConsultationAppointment, setIsConsultationAppointment] = useState(false);
  const [availableTimeSlots] = useState(getTimeIntervals());
  const [timeFrom, setTimeFrom] = useState(availableTimeSlots[0]);
  const [timeTo, setTimeTo] = useState(availableTimeSlots[0]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [forceConfirmModalOpen, setForceConfirmModalOpen] = useState(false);
  const [forceConfirmData, setForceConfirmData] = useState(null);
  const [eligibleAppointmentTypes, setEligibleAppointmentTypes] = useState([]);
  const [bookingEligibilityMissing, setBookingEligibilityMissing] = useState(false);
  const [eligibilityCheckLoading, setEligibilityCheckLoading] = useState(false);
  const finishLoadingCalledRef = useRef(false);

  useEffect(() => {
    if (forceConfirmModalOpen && isOverwriting && !finishLoadingCalledRef.current) {
      finishLoadingCalledRef.current = true;
      finishOverwriteTimeslot();
    }
  }, [forceConfirmModalOpen, isOverwriting, finishOverwriteTimeslot]);

  useEffect(() => {
    if (!forceConfirmModalOpen) {
      finishLoadingCalledRef.current = false;
    }
  }, [forceConfirmModalOpen]);

  const isNewRegistrationOrPendingRegistration =
    selectedAppointmentType?.donorStatus === "new" ||
    selectedAppointmentType?.donorStatus === "pending";

  const resourceTimeslots = useMemo(() => {
    if (!calendar) return [];
    const resourceId = selectedTimeslot?.resourceId || selectedResourceId;
    if (!resourceId) return [];
    const resource = calendar.find((res) => res.resourceId === resourceId);
    return resource?.timeslots || [];
  }, [calendar, selectedResourceId, selectedTimeslot]);

  const getOverlapInfo = ({
    bookingStart,
    bookingEnd,
    currentTimeslotId,
    includeSelf,
  }) => {
    const overlaps = [];
    resourceTimeslots.forEach((slot) => {
      (slot.appointments || []).forEach((app) => {
        const isSameAppointment =
          app.startDate === bookingStart && slot.timeslotId === currentTimeslotId;
        if (!includeSelf && isSameAppointment) return;
        const appStart = app.startDate;
        const appEnd = app.endDate;
        const isOverlap = appStart < bookingEnd && appEnd > bookingStart;
        if (isOverlap) {
          overlaps.push({
            timeslot: slot,
            appointment: app,
            status: app.status,
            hasReservation: !!app.reservation,
          });
        }
      });
    });
    const isReserved = (item) =>
      item.hasReservation || (item.status && item.status !== "pending");

    const reservedOverlaps = overlaps.filter(isReserved);
    const availableOverlaps = overlaps.filter((item) => !isReserved(item));

    return { reservedOverlaps, availableOverlaps };
  };

  const addExceptionToTimeslot = async (slot, overlapAppointments) => {
    const base = {...prepareTimeslotForUpdating(slot), rule: slot.initialSettings};
    const existingExceptions = base.exceptionHoursOnDates || [];
    const updatedExceptions = [...existingExceptions];
    

    overlapAppointments.forEach(({ appointment }) => {
      const exception = {
        from: timestampToIso(appointment.startDate),
        to: timestampToIso(appointment.endDate),
        date: timestampToIso(getStartOfWorkDay(appointment.startDate)),
      };
      updatedExceptions.push(exception);
    });

    const payload = {
      ...base,
      exceptionHoursOnDates: updatedExceptions,
    };

    await setTimeslotRequest(payload, slot.appointmentType?.duration);
  };

  const applyOverlapExceptions = async (availableOverlaps) => {
    if (!availableOverlaps || availableOverlaps.length === 0) return;
    const grouped = availableOverlaps.reduce((acc, item) => {
      const key = item.timeslot.timeslotId;
      acc[key] = acc[key] ? [...acc[key], item] : [item];
      return acc;
    }, {});

    for (const key of Object.keys(grouped)) {
      await addExceptionToTimeslot(grouped[key][0].timeslot, grouped[key]);
    }
  };

  const handleOverlaps = async ({
    bookingStart,
    bookingEnd,
    currentTimeslotId,
    applyAvailableExceptions,
    deferExceptions = false,
  }) => {
    if(!isSlotClickFromCell && isConsultationAppointment) {
      return { blocked: false, hadAvailableOverlaps: false, deferredOverlaps: [] };
    }

    const { reservedOverlaps, availableOverlaps } = getOverlapInfo({
      bookingStart,
      bookingEnd,
      currentTimeslotId,
      includeSelf: !!applyAvailableExceptions,
    });

    if (reservedOverlaps.length > 0) {
      setOverlapModalOpen(true);
      return { blocked: true, hadAvailableOverlaps: false, deferredOverlaps: [] };
    }

    const exceptableOverlaps = applyAvailableExceptions
      ? availableOverlaps
      : availableOverlaps.filter((item) => item.timeslot.timeslotId !== currentTimeslotId);

    if (exceptableOverlaps.length > 0 && !deferExceptions) {
      await applyOverlapExceptions(exceptableOverlaps);
    }

    return {
      blocked: false,
      hadAvailableOverlaps: availableOverlaps.length > 0,
      deferredOverlaps: deferExceptions ? exceptableOverlaps : [],
    };
  };

  useEffect(() => {
    if(selectedResourceId && selectedAppointmentType) {
      const newTimeFrom = availableTimeSlots.find(item => item.value === selectedTime) || availableTimeSlots[0];
      setTimeFrom(newTimeFrom);
      const newTimeToInMilliseconds = workingTimeToTimestamp(newTimeFrom, currentDate) + selectedAppointmentType.duration;
      setTimeTo(timestampToWorkingTime(newTimeToInMilliseconds));
    }
  }, [availableTimeSlots, currentDate, selectedAppointmentType, selectedResourceId, selectedTime])

  useEffect(() => {
    if (userPnr) {
      getUserByPnr(userPnr);
    }
  }, [getUserByPnr, userPnr]);

  useEffect(() => {
    setCurrentUser(userByPnr ? userByPnr : null);
  }, [userByPnr]);

  useEffect(() => {
    if (currentUser && !isSlotClickFromCell) {
      setIsDonorStatusPending(currentUser?.status === "pending");
    }

    if(currentUser && isSlotClickFromCell) {
      setIsConsultationAppointment(currentUser?.status === "pending");
      setIsDonorStatusPending(currentUser?.status === "pending");
    }
  }, [currentDate, currentUser, isConsultationAppointment, isSlotClickFromCell, selectedTimeslot, currentAppointment]);

  useEffect(() => {
    let isMounted = true;
    const setDefaults = (mappedTypes, hasMissingEligibility = false) => {
      if (!currentUser) {
        setSelectedAppointmentType(null);
        setBookingEligibilityMissing(false);
        setEligibilityCheckLoading(false);
        return;
      }
      
      if (hasMissingEligibility) {
        setBookingEligibilityMissing(true);
        setSelectedAppointmentType(null);
        setEligibilityCheckLoading(false);
        return;
      }
      
      if (mappedTypes && mappedTypes.length > 0) {
        setBookingEligibilityMissing(false);
        setEligibilityCheckLoading(false);
        return;
      }
      
      const fallback = appointmentTypes.find(
        (item) => item.donorStatus === currentUser.status
      );
      if (fallback) {
        setSelectedAppointmentType(fallback);
        setBookingEligibilityMissing(false);
      }
      setEligibilityCheckLoading(false);
    };

    if (currentUser) {
      setEligibilityCheckLoading(true);

      (async () => {
        try {
          const res = await get(`/timeslots/getEligibleAppointmentTypesForStaff/${currentUser.id}`);
          const items = (res?.content || []).map(entry => ({
            ...entry.appointmentType,
            bookingChecks: entry.bookingChecks || entry.appointmentType?.bookingChecks || null,
            bookableFromDate: entry.bookableFromDate || null,
            donorQuarantine: entry.donorQuarantine || null,
            eligibility: entry.eligibility || null,
          }));
          if (!isMounted) return;
          setEligibleAppointmentTypes(items);
          setDefaults(items, false);
        } catch (err) {
          const isMissingEligibility = err?.body?.error === 'booking_eligibility_missing';
          if (!isMounted) return;
          setEligibleAppointmentTypes([]);
          setDefaults([], isMissingEligibility);
        }
      })();
    } else {
      setEligibleAppointmentTypes([]);
      setDefaults([], false);
    }

    return () => { isMounted = false };
  }, [currentUser, currentDate, appointmentTypes]);

  useEffect(() => {
    setCurrentAppointments(
      selectedTimeslot?.appointments?.filter((item) => item.status === "pending")
        .map((item) => {
          return {
            label: `${dateFns.format(new Date(item.startDate), "HH:mm")} - ${dateFns.format(new Date(item.endDate), "HH:mm")}`,
            value: String(item.startDate),
          };
        }) || [],
    );

    setIsConsultationAppointment(selectedTimeslot?.appointmentType.name === "Consultation" && !isSlotClickFromCell);

  }, [selectedTimeslot, isSlotClickFromCell]);

  const getTimeslotLimits = () => {
    const startDate = workingTimeToTimestamp(timeFrom, currentDate);
    const endDate = workingTimeToTimestamp(timeTo, currentDate);
    return { startDate, endDate };
  };

  const getTimeslotRequestBody = (checkForAvailableResources) => {
    if (!selectedAppointmentType) {
      return null;
    }
    const { startDate, endDate } = getTimeslotLimits();
    const bloodTypes = isNewRegistrationOrPendingRegistration || !currentUser?.donorType
      ? ALL_BLOOD_TYPES
      : [currentUser.donorType];
    const bloodTiter =
      currentUser?.status === "established" &&
      currentUser?.titer === "low" &&
      currentUser?.donorType?.bloodType === "o" &&
      currentUser?.donorType?.rhesus === "plus"
        ? [currentUser?.bloodTiter]
        : BLOOD_TITER;

    return {
      ...(!checkForAvailableResources && {
        resourceId: selectedResourceId || selectedTimeslot?.resourceId,
      }),
      donorType: bloodTypes,
      bloodTiter,
      appointmentTypeId: selectedAppointmentType.appointmentTypeId,
      startDate,
      endDate,
      exceptionDates: [],
      exceptionRules: [],
      exceptionHoursOnDates: [],
    };
  };

  const handleSubmit = async () => {
    if (!selectedAppointmentType) {
      return;
    }

    const snapshotUser = currentUser;
    const snapshotAppointmentType = selectedAppointmentType;
    const snapshotIsConsultation = isConsultationAppointment;
    const snapshotIsDonorStatusPending = isDonorStatusPending;

    toggleBookAppointmentModal();
    startOverwriteTimeslot();

    const hasExistingAppointment = snapshotUser?.nextAppointment !== undefined && snapshotUser?.nextAppointment !== null;

    const cancelExistingAppointment = async () => {
      if (hasExistingAppointment) {
        await cancelAppointmentBooking(
          bloodBankId,
          snapshotUser.nextAppointment.reservationId,
          snapshotUser.id,
          snapshotUser.nextAppointment.startDate,
          bloodBankName,
        );
      }
    };

    const snapshotUpdateDonorStatus = async () => {
      try {
        if (snapshotIsConsultation && !snapshotIsDonorStatusPending) {
          await updateDonor({
            ...snapshotUser,
            status: "pending",
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

    const mergeExistingAppointment = (failedChecks = [], details = {}, overrideAllowed = true) => {
      if (!hasExistingAppointment) {
        return { failedChecks, details, overrideAllowed };
      }
      return {
        failedChecks: [...failedChecks, 'existingAppointment'],
        details: {
          ...details,
          existingAppointment: {
            ok: false,
            value: snapshotUser.nextAppointment.startDate,
            overrideAllowed: true,
          },
        },
        overrideAllowed,
      };
    };

    let timeSlotData = selectedTimeslot;
    let requestBodyData = getTimeslotRequestBody();

    const appointmentValue = isSlotClickFromCell ? requestBodyData?.startDate : chosenAppointment.value;
    const bookingStart = Number(appointmentValue);
    const bookingEnd = bookingStart + snapshotAppointmentType.duration;

    const needsCustomTimeslot =
      !isSlotClickFromCell &&
      (snapshotAppointmentType.appointmentTypeId !== timeSlotData?.appointmentType?.appointmentTypeId ||
        snapshotAppointmentType.duration !== timeSlotData?.appointmentType?.duration);

    if (needsCustomTimeslot && requestBodyData) {
      requestBodyData = {
        ...requestBodyData,
        startDate: bookingStart,
        endDate: bookingEnd,
      };
    }

    const mergeOverwriteWarning = (failedChecks = [], details = {}, overrideAllowed = true) => {
      if (!needsCustomTimeslot) {
        return { failedChecks, details, overrideAllowed };
      }
      return {
        failedChecks: ['appointmentTypeMismatch', ...failedChecks],
        details: {
          ...details,
          appointmentTypeMismatch: {
            ok: false,
            originalType: timeSlotData?.appointmentType?.name || '',
            selectedType: snapshotAppointmentType?.name || '',
            overrideAllowed: true,
          },
        },
        overrideAllowed,
      };
    };

    const overlapResult = await handleOverlaps({
      bookingStart,
      bookingEnd,
      currentTimeslotId: timeSlotData?.timeslotId,
      applyAvailableExceptions: needsCustomTimeslot,
      deferExceptions: isSlotClickFromCell || needsCustomTimeslot,
    });
    if (overlapResult?.blocked) {
      finishOverwriteTimeslot();
      return;
    };
    const deferredOverlaps = overlapResult?.deferredOverlaps || [];

    if (isSlotClickFromCell || needsCustomTimeslot) {
      let serverFailedChecks = [];
      let serverDetails = {};
      let serverOverrideAllowed = true;
      let hasServerFailure = false;

      try {
        const preCheckResult = await preCheckBooking({
          donorUid: snapshotUser.id,
          appointmentTypeId: snapshotAppointmentType.appointmentTypeId,
          appointmentStartDate: bookingStart,
          donorType: requestBodyData?.donorType,
          bloodTiter: requestBodyData?.bloodTiter,
        });

        if (preCheckResult?.content?.canBook === false) {
          const result = preCheckResult.content;
          serverFailedChecks = result.failedChecks || [];
          serverDetails = result.details || {};
          serverOverrideAllowed = !!result.overrideAllowed;
          hasServerFailure = true;
        }
      } catch (preCheckErr) {
        if (preCheckErr?.body?.error === 'failed_checks') {
          serverFailedChecks = preCheckErr.body.failedChecks || [];
          serverDetails = preCheckErr.body.details || {};
          serverOverrideAllowed = !!preCheckErr.body.overrideAllowed;
          hasServerFailure = true;
        } else {
          finishOverwriteTimeslot();
          throw preCheckErr;
        }
      }

      if (hasServerFailure || hasExistingAppointment || needsCustomTimeslot) {
        const withOverwrite = mergeOverwriteWarning(serverFailedChecks, serverDetails, serverOverrideAllowed);
        const merged = mergeExistingAppointment(withOverwrite.failedChecks, withOverwrite.details, withOverwrite.overrideAllowed);
        setForceConfirmData({
          failedChecks: merged.failedChecks,
          details: merged.details,
          overrideAllowed: merged.overrideAllowed,
          userId: snapshotUser?.id,
          bloodBankId,
          bookingStart,
          onForceBookCustom: async () => {
            startOverwriteTimeslot();
            try {
              await cancelExistingAppointment();
              await applyOverlapExceptions(deferredOverlaps);
              const tsData = requestBodyData;
              if (isSlotClickFromCell) setIsDirectlyBooking(true);
              const created = await setTimeslotRequest(
                tsData,
                snapshotAppointmentType.duration,
                true,
              );
              if (!created?.timeslotId) throw new Error('Timeslot creation failed');
              await bookAppointment(
                bookingStart,
                created.timeslotId,
                snapshotUser.id,
                bloodBankId,
                true,
              );
              await snapshotUpdateDonorStatus();
            } finally {
              finishOverwriteTimeslot();
              setIsDirectlyBooking(false);
            }
          },
        });
        finishOverwriteTimeslot();
        setIsDirectlyBooking(false);
        setForceConfirmModalOpen(true);
        return;
      }

      await applyOverlapExceptions(deferredOverlaps);

      if (isSlotClickFromCell) {
        setIsDirectlyBooking(true);
        timeSlotData = await setTimeslotRequest(
          requestBodyData,
          snapshotAppointmentType.duration,
          true,
        );
        if (timeSlotData) {
          timeSlotData.appointments = [
            {
              appointmentType: timeSlotData.appointmentType,
              bloodTiter: timeSlotData.bloodTiter,
              donorType: timeSlotData.donorType,
              startDate: timeSlotData.startDate,
              endDate: timeSlotData.endDate,
              startDateISO: timeSlotData.startDateISO,
              status: "pending",
              timeslotId: timeSlotData.timeslotId,
              timeslotStartDate: timeSlotData.startDate,
              titer: timeSlotData.bloodTiter,
            },
          ];
        }
      }

      if (needsCustomTimeslot) {
        const data = requestBodyData;
        const createdTimeslot = await setTimeslotRequest(
          data,
          snapshotAppointmentType.duration,
          true,
        );
        if (!createdTimeslot?.timeslotId) return;

        await bookAppointment(
          bookingStart,
          createdTimeslot.timeslotId,
          snapshotUser.id,
          bloodBankId,
        );
        await snapshotUpdateDonorStatus();
        finishOverwriteTimeslot();
        return;
      }
    }

    if (hasExistingAppointment) {
      const merged = mergeExistingAppointment([], {}, true);
      setForceConfirmData({
        failedChecks: merged.failedChecks,
        details: merged.details,
        overrideAllowed: merged.overrideAllowed,
        userId: snapshotUser?.id,
        timeslotId: timeSlotData?.timeslotId,
        bloodBankId,
        bookingStart,
        onForceBookCustom: async () => {
          startOverwriteTimeslot();
          try {
            await cancelExistingAppointment();
            await bookAppointment(
              bookingStart,
              timeSlotData?.timeslotId,
              snapshotUser.id,
              bloodBankId,
            );
            await snapshotUpdateDonorStatus();
          } finally {
            finishOverwriteTimeslot();
          }
        },
      });
      finishOverwriteTimeslot();
      setIsDirectlyBooking(false);
      setForceConfirmModalOpen(true);
      return;
    }

    try {
      await bookAppointment(
        bookingStart,
        timeSlotData?.timeslotId,
        snapshotUser.id,
        bloodBankId,
      );
    } catch (err) {
      if (err && err.body && err.body.error === 'failed_checks') {
        const merged = mergeExistingAppointment(
          err.body.failedChecks || [],
          err.body.details || {},
          !!err.body.overrideAllowed,
        );
        setForceConfirmData({
          failedChecks: merged.failedChecks,
          details: merged.details,
          overrideAllowed: merged.overrideAllowed,
          userId: snapshotUser?.id,
          timeslotId: timeSlotData?.timeslotId,
          bloodBankId,
          bookingStart,
          snapshotUpdateDonorStatus,
        });
        finishOverwriteTimeslot();
        setIsDirectlyBooking(false);
        setForceConfirmModalOpen(true);
        return;
      }
      finishOverwriteTimeslot();
      throw err;
    }

    await snapshotUpdateDonorStatus();
    finishOverwriteTimeslot();
  };

  const handleModalClose = () => {
    toggleBookAppointmentModal();
  };

  useEffect(() => {
    const initialChosenAppointmentValue = isDayPage ? currentAppointments.find(appointment => Number(appointment.value) === currentAppointment?.startDate) : null;
    
    setUserPnr("");
    setCurrentUser(null);
    setIsDonorStatusPending(false);
    setIsConsultationAppointment(false);
    setSelectedAppointmentType(null);
    setEligibleAppointmentTypes([]);
    setEligibilityCheckLoading(false);
    setBookingEligibilityMissing(false);
    if (!bookAppointmentModalIsOpen) {
      setChosenAppointment(initialChosenAppointmentValue);
    } else {
      if (currentAppointments.length === 1) {
        setChosenAppointment(currentAppointments[0]);
      }
    }
  }, [bookAppointmentModalIsOpen, currentAppointments, currentAppointment?.startDate, isDayPage]);

  const formatText = (text) => {
    const firstPart = text.slice(0, 6);
    const secondPart = text.slice(6, 11);
    return `${firstPart}\u00A0${secondPart}`;
  };

  const handleCopy = (e) => {
    e.clipboardData.setData("text/plain", userPnr);
    e.preventDefault();
  };

  const isBookDisabled = () => {
    if (eligibilityCheckLoading) {
      return true;
    }

    if (bookingEligibilityMissing) {
      return true;
    }

    if (currentUser === null) {
      return true;
    }

    if (eligibleAppointmentTypes.length > 0 && !selectedAppointmentType) {
      return true;
    }

    if (isConsultationAppointment) {
      return false;
    }

    if (!selectedAppointmentType) {
      return true;
    }

    return !isSlotClickFromCell && chosenAppointment === null;
  };

  return (
    <>
      <Modal
        isModalOpen={bookAppointmentModalIsOpen}
        handleModalClose={handleModalClose}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t("configuration.settings.bookAppointmentModal.header")}
          </h2>
          <button className="modal-close" onClick={handleModalClose}>
            <span className="icon-close" />
          </button>
        </div>

        <div className="modal-body modal-content">
          <div className="form-group d-flex items-center">
            <div className="flex-grow-0 modal-label">
              {t("configuration.settings.bookAppointmentModal.id")}
            </div>
            <div className="flex-grow-1">
              <Search
                type="text"
                placeholder={t(
                  "configuration.settings.bookAppointmentModal.searchPlaceholder"
                )}
                shortSearch={true}
                setValue={setUserPnr}
                autoComplete={"off"}
                isPnrSearch={true}
              />
            </div>
          </div>

          <div className={`form-group modal-text ${currentUser || "thin"}`}>
            {currentUser ? (
              `${currentUser.firstname} ${currentUser.lastname}`
            ) : userPnr === "" ? (
              ""
            ) : (
              <>
                <span>
                  {t("configuration.settings.bookAppointmentModal.noMatch")}
                </span>
                <span onCopy={handleCopy}>{` ${formatText(userPnr)}`}</span>
              </>
            )}
          </div>

          {currentAppointments.length > 1 && currentUser && (
            <div className="form-group">
              <CustomSelect
                label={t(
                  "configuration.settings.bookAppointmentModal.selectAppointmentTime"
                )}
                data={currentAppointments}
                value={chosenAppointment}
                onChange={setChosenAppointment}
                isSearchable={true}
              />
            </div>
          )}

          {/* Appointment type selector: use server-provided eligible types when donor is loaded */}
          {currentUser && eligibleAppointmentTypes.length > 0 && (
            <div className="form-group">
              <CustomSelect
                label={t("configuration.settings.bookAppointmentModal.appointmentType")}
                data={[
                  { label: t("configuration.settings.bookAppointmentModal.appointmentTypeSelect"), value: null, isPlaceholder: true },
                  ...eligibleAppointmentTypes.map((it) => ({ label: getAppointmentTypeDisplayName(it), value: it }))
                ]}
                value={selectedAppointmentType ? { label: getAppointmentTypeDisplayName(selectedAppointmentType), value: selectedAppointmentType } : null}
                onChange={(val) => setSelectedAppointmentType(val?.value || null)}
                isSearchable={true}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value?.appointmentTypeId || opt.value?.name || 'placeholder'}
              />
            </div>
          )}

          {bookingEligibilityMissing && (
            <div className="form-group modal-text thin" style={{ color: '#d0021b', fontWeight: 600 }}>
              {t("configuration.settings.bookAppointmentModal.bookingEligibilityMissing")}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.cancel")}
                onClick={toggleBookAppointmentModal}
              />
            </div>
            <div className="inputs-group-item">
              <Button
                disabled={isBookDisabled()}
                btnClassName="btn-primary"
                type="button"
                text={t("common.book")}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isModalOpen={overlapModalOpen}
        handleModalClose={() => setOverlapModalOpen(false)}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t("configuration.settings.bookAppointmentModal.overlapTitle") ||
              "Cannot book appointment"}
          </h2>
          <button
            className="modal-close"
            onClick={() => setOverlapModalOpen(false)}
          >
            <span className="icon-close" />
          </button>
        </div>
        <div className="modal-body modal-content">
          <div className="modal-msg">
            {t("configuration.settings.bookAppointmentModal.overlapReserved")}
          </div>
        </div>
        <div className="modal-footer light w-100">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.ok")}
                btnClassName="m-10"
                onClick={() => setOverlapModalOpen(false)}
              />
            </div>
          </div>
        </div>
      </Modal>
      <ForceConfirmModal
        isOpen={forceConfirmModalOpen}
        failedChecks={forceConfirmData?.failedChecks}
        details={forceConfirmData?.details}
        overrideAllowed={forceConfirmData?.overrideAllowed}
        userId={forceConfirmData?.userId}
        timeslotId={forceConfirmData?.timeslotId}
        bloodBankId={forceConfirmData?.bloodBankId}
        bookingStart={forceConfirmData?.bookingStart}
        onForceBookCustom={forceConfirmData?.onForceBookCustom}
        onClose={() => setForceConfirmModalOpen(false)}
        onSuccess={() => {
          setForceConfirmModalOpen(false);
          if (!forceConfirmData?.onForceBookCustom) {
            forceConfirmData?.snapshotUpdateDonorStatus?.();
          }
        }}
        onError={(err) => {
          setForceConfirmModalOpen(false);
          finishOverwriteTimeslot();
        }}
      />
    </>
  );
};

export default BookAppointment;
